import { Flex, Text, TextInput } from '@tremor/react';
import s from './ShopifyConnector.module.scss';
import { useConnectorAuth } from '@/hooks/useConnectorAuth';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLoaderData } from 'react-router-dom';
import { ConnectorFromStore } from '@/types/Connectors';
import { ConnectorTitle } from '../ConnectorTitle/ConnectorTitle';
import { ConnectionSteps } from '@/components/ConnectionSteps/ConnectionSteps';
import { SHOPIFY_CONNECTION_STEPS } from '@/utils/constants';
import { ConnectorSubtitle } from '../ConnectorSubtitle/ConnectorSubtitle';
import { BottomActions } from '../BottomActions/BottomActions';

const defaultValChecked = 'oauth';
const scope = 'read_customers,read_discounts,read_orders,read_products';

const schema = z.object({
  shopDomain: z
    .string()
    .min(1, 'Please enter a valid shop domain')
    .transform((val) => `https://${val}.myshopify.com`)
    .pipe(z.string().url('Please enter a valid shop domain')),
});

type Schema = z.infer<typeof schema>;

export function ShopifyInfoConnector() {
  const connector = useLoaderData() as ConnectorFromStore;
  const [authUrl, setAuthUrl] = useState('');
  const [inputVal, setInputVal] = useState('');
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      shopDomain: connector ? connector.config.accounts?.[0]?.id || '' : '',
    },
  });
  useEffect(() => {
    if (inputVal) {
      setAuthUrl(`https://${inputVal}.myshopify.com/admin/oauth/authorize`);
    }
  }, [inputVal]);

  const [, , isLoading, generateOAuthLink] = useConnectorAuth(
    defaultValChecked,
    {
      platform: 'shopify',
      OAuthUrl: authUrl,
      clientID: import.meta.env._VITE_SHOPIFY_APP_ID,
    },
    inputVal,
  );

  const onSubmit = async () => {
    window.location.href = await generateOAuthLink(scope);
  };

  return (
    <form className={s.container} onSubmit={handleSubmit(onSubmit)}>
      <ConnectorTitle>Shopify</ConnectorTitle>
      <ConnectionSteps
        style={{ marginBottom: '40px' }}
        steps={SHOPIFY_CONNECTION_STEPS}
        currentStep={1}
      />
      <ConnectorSubtitle>Enter your Shopify store ID</ConnectorSubtitle>
      <Flex className={s.shopDomainInputContainer}>
        <Text className={s.inputDisabledParts}>https://</Text>
        <div className={s.inputErrorMessage}>
          <Controller
            name="shopDomain"
            control={control}
            render={({ field: { onChange, ...rest } }) => (
              <TextInput
                {...rest}
                className={s.shopInput}
                error={!!errors.shopDomain?.message}
                errorMessage={errors.shopDomain?.message}
                onChange={(e) => {
                  setInputVal(e.currentTarget.value);
                  onChange(e);
                }}
                placeholder="Your Shopify Store URL"
              />
            )}
          />
        </div>
        <Text className={s.inputDisabledParts}>.myshopify.com</Text>
      </Flex>
      <BottomActions
        isReconnectable={false}
        connectorId={connector?.id}
        isCreating={!connector}
        isLoading={isLoading}
      />
    </form>
  );
}
